/* WEBMCP - HANDING AN AGENT THE PAGE'S OWN ACTIONS
   The page declares what it can do as named, typed tools. This block is the
   shared plumbing for the map and article tool sets below:
   1. navigator.modelContext exists only where WebMCP is enabled, so one guard
      keeps every other browser from a TypeError on load - and from the
      catalog fetch below.
   2. ONE SOURCE OF TRUTH. Each tool's name, description, input schema and
      annotations live in resources/webmcp/tools.json, served (with Site
      settings applied) as /.well-known/webmcp.json by
      WebmcpCatalogController. register() fetches that catalog and pairs each
      execute() here with its entry, so an outside agent reading the catalog
      and one on the page see the same tools word for word. A tool the
      catalog leaves out (a feature switched off) is not registered.
   3. Two API shapes: per-tool registerTool() (spec) and provideContext({tools})
      (@mcp-b polyfill, earlier drafts). Each page registers once, with its
      own tools and the shared ones (commonTools()).
   4. Each execute() is wrapped so a throw becomes a readable error result.
   Results use MCP's shape, { content: [{ type: 'text', text }] }, built by
   text() and failure().

   The pure helpers (siteClock, openStatus, placePasses, tagCounts,
   withCatalog...) take everything as arguments, so
   tests/js/mcp-tools.test.mjs lifts and runs the shipping code. */

window.hcAgentTools = (function () {
  'use strict';

  const CATALOG_URL = '/.well-known/webmcp.json';

  // Monday first, as OpeningHours::DAYS and the `hours` field.
  const HOURS_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Intl.DateTimeFormat per timezone, built once - see siteClock().
  const CLOCK_FORMATS = {};

  function text(value) {
    return {
      content: [{
        type: 'text',
        text: typeof value === 'string' ? value : JSON.stringify(value, null, 2),
      }],
    };
  }

  function failure(message) {
    return { isError: true, content: [{ type: 'text', text: message }] };
  }

  /** A `limit` argument, clamped to 1..max. */
  function limitOf(value, fallback, max) {
    return Math.min(Math.max(parseInt(value, 10) || fallback, 1), max);
  }

  /** The site's timezone (Site settings, on <html data-timezone>). */
  function siteTimeZone() {
    return document.documentElement.dataset.timezone || 'America/Edmonton';
  }

  /**
   * The weekday and minute of the day at `date` in `timeZone` - the site's
   * clock, whatever the visitor's own zone. An unknown zone falls back to the
   * visitor's, as siteWeekday() in app.js does.
   *
   * @returns {{day: string, minutes: number, time: string}}
   */
  function siteClock(date, timeZone) {
    const key = timeZone || '';

    // One formatter per zone: open now asks this for every place.
    if (!CLOCK_FORMATS[key]) {
      const options = { weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

      try {
        CLOCK_FORMATS[key] = new Intl.DateTimeFormat('en-US', Object.assign({ timeZone: timeZone || undefined }, options));
      } catch (e) {
        CLOCK_FORMATS[key] = new Intl.DateTimeFormat('en-US', options);
      }
    }

    const parts = CLOCK_FORMATS[key].formatToParts(date);
    const part = type => (parts.find(p => p.type === type) || {}).value || '';
    const hour = (parseInt(part('hour'), 10) || 0) % 24;
    const minute = parseInt(part('minute'), 10) || 0;

    return {
      day: part('weekday').toLowerCase(),
      minutes: hour * 60 + minute,
      time: String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0'),
    };
  }

  /**
   * Open or closed at `date`, by a place's `hours` field (see
   * App\Support\OpeningHours: Monday-first keys, "HH:MM-HH:MM" ranges, a
   * close at or before the open running past midnight, "24:00" for
   * midnight). Shifts are laid out from yesterday to a week ahead and joined
   * where they touch, so "18:00-24:00" then "00:00-02:00" is one stretch.
   * Null when the hours are unknown (no field, or not one opening all week).
   *
   * @returns {null|{open: boolean, alwaysOpen: boolean, now: {day: string, time: string},
   *   closesAt: ?{day: string, time: string}, opensAt: ?{day: string, time: string}}}
   */
  function openStatus(hours, date, timeZone) {
    if (!hours || typeof hours !== 'object') return null;

    const clock = siteClock(date, timeZone);
    const today = HOURS_DAYS.indexOf(clock.day);

    if (today === -1) return null;

    const DAY = 1440;
    const stretches = [];

    for (let offset = -1; offset <= 7; offset++) {
      const ranges = hours[HOURS_DAYS[(today + offset + 7) % 7]];

      (Array.isArray(ranges) ? ranges : []).forEach((range) => {
        const m = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/.exec(String(range).replace(/\s+/g, ''));

        if (!m) return;

        const from = (+m[1]) * 60 + (+m[2]);
        let to = (+m[3]) * 60 + (+m[4]);

        if (to <= from) to += DAY;

        stretches.push([offset * DAY + from, offset * DAY + to]);
      });
    }

    if (!stretches.length) return null;

    stretches.sort((a, b) => a[0] - b[0]);

    const joined = [];

    stretches.forEach((s) => {
      const last = joined[joined.length - 1];

      if (last && s[0] <= last[1]) last[1] = Math.max(last[1], s[1]);
      else joined.push(s.slice());
    });

    const at = (minute) => {
      const dayOffset = Math.floor(minute / DAY);
      const within = ((minute % DAY) + DAY) % DAY;

      return {
        day: HOURS_DAYS[(((today + dayOffset) % 7) + 7) % 7],
        time: String(Math.floor(within / 60)).padStart(2, '0') + ':' + String(within % 60).padStart(2, '0'),
      };
    };

    const current = joined.find(s => s[0] <= clock.minutes && clock.minutes < s[1]);
    // Open from before yesterday's start to past next week: around the clock.
    const alwaysOpen = !!current && current[0] <= -DAY && current[1] >= 8 * DAY;
    const next = current ? null : joined.find(s => s[0] > clock.minutes);

    return {
      open: !!current,
      alwaysOpen,
      now: { day: clock.day, time: clock.time },
      closesAt: current && !alwaysOpen ? at(current[1]) : null,
      opensAt: next ? at(next[0]) : null,
    };
  }

  /** "$".."$$$$" as 1..4; 0 when the place has no price level. */
  function priceLevel(p) {
    const price = String((p && p.price) || '').trim();

    return /^\$+$/.test(price) ? Math.min(price.length, 4) : 0;
  }

  /**
   * Whether a place passes search_places' filters. `env` carries what the
   * filters need from the page: the amenity field map, the text matcher,
   * slugs, the hours by slug, the clock, and a distance function that answers
   * null when there is no point to measure from.
   */
  function placePasses(p, f, env) {
    if (f.query && !env.matches(p, f.query)) return false;
    if (f.amenities && f.amenities.length && !f.amenities.every(key => p[env.fields[key]])) return false;

    if (f.cuisine && !(Array.isArray(p.cuisines) && p.cuisines.some(c => String(c).toLowerCase() === f.cuisine))) return false;
    if (f.featuredOnly && !p.featured) return false;

    if (f.maxPrice) {
      const level = priceLevel(p);

      if (!level || level > f.maxPrice) return false;
    }

    if (f.radiusKm) {
      const km = env.distanceKm(p);

      if (km === null || km > f.radiusKm) return false;
    }

    if (f.openNow) {
      const status = openStatus((env.hours || {})[env.slug(p)], env.now, env.timeZone);

      if (!status || !status.open) return false;
    }

    return true;
  }

  /** How many articles carry each tag slug - every slug given, zero included. */
  function tagCounts(articles, slugs) {
    const counts = {};

    (slugs || []).forEach((slug) => { counts[slug] = 0; });
    (articles || []).forEach((article) => {
      (Array.isArray(article.tags) ? article.tags : []).forEach((slug) => {
        if (Object.prototype.hasOwnProperty.call(counts, slug)) counts[slug]++;
      });
    });

    return counts;
  }

  /**
   * The page's tools paired with the catalog: each takes the catalog's
   * description, schema and annotations; one the catalog does not list is
   * dropped.
   */
  function withCatalog(tools, catalog) {
    const byName = {};

    (Array.isArray(catalog) ? catalog : []).forEach((entry) => {
      if (entry && entry.name) byName[entry.name] = entry;
    });

    return (tools || []).filter(tool => byName[tool.name]).map((tool) => {
      const entry = byName[tool.name];
      const out = Object.assign({}, tool, {
        description: entry.description,
        inputSchema: entry.inputSchema || { type: 'object', properties: {} },
      });

      if (entry.annotations) out.annotations = entry.annotations;

      return out;
    });
  }

  let catalogRequest = null;

  // Memoised for the page; no-store, so a settings change shows on reload.
  function loadCatalog() {
    if (!catalogRequest) {
      catalogRequest = fetch(CATALOG_URL, { cache: 'no-store' })
        .then(res => (res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status))))
        .then(body => (body && Array.isArray(body.tools) ? body.tools : []))
        .catch((err) => {
          catalogRequest = null;
          throw err;
        });
    }

    return catalogRequest;
  }

  /**
   * Registers a set of tools, if this browser has WebMCP at all.
   *
   * @param {Array<{name, execute}>} tools
   * @returns {Promise<boolean>} whether anything was registered
   */
  async function register(tools) {
    const ctx = navigator.modelContext;

    if (!ctx || !Array.isArray(tools) || !tools.length) return false;

    let listed;

    try {
      listed = withCatalog(tools, await loadCatalog());
    } catch (err) {
      if (window.console) console.warn('[webmcp] the tool catalog could not be loaded, so no tools:', err);

      return false;
    }

    if (!listed.length) return false;

    const safe = listed.map(tool => Object.assign({}, tool, {
      async execute(args) {
        try {
          return await tool.execute(args || {});
        } catch (err) {
          return failure('The "' + tool.name + '" tool failed: ' + (err && err.message ? err.message : String(err)));
        }
      },
    }));

    try {
      if (typeof ctx.registerTool === 'function') {
        safe.forEach(tool => ctx.registerTool(tool));

        return true;
      }

      if (typeof ctx.provideContext === 'function') {
        ctx.provideContext({ tools: safe });

        return true;
      }
    } catch (err) {
      if (window.console) console.error('[webmcp] registration failed:', err);
    }

    return false;
  }

  /* ---- Shared by every page: the topbar and the announcements bar ---- */

  function absolute(href) {
    try {
      return href ? new URL(href, window.location.href).href : null;
    } catch (e) {
      return null;
    }
  }

  function isDark() {
    return document.documentElement.classList.contains('dark');
  }

  function commonTools() {
    return [
      {
        name: 'get_announcements',
        async execute() {
          const cards = Array.prototype.slice.call(document.querySelectorAll('#announcements .announcement-card'));
          const read = (card, selector) => {
            const node = card.querySelector(selector);

            return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
          };

          return text({
            total: cards.length,
            announcements: cards.map(card => ({
              title: read(card, '.announcement-title'),
              text: read(card, '.announcement-body') || null,
              // Through /announcements/<id>/open, which counts the click as a visitor's would.
              url: absolute(card.getAttribute('href') || card.getAttribute('data-announcement-image')),
            })),
          });
        },
      },
      {
        name: 'set_theme',
        async execute(args) {
          const wanted = args.theme === 'toggle' ? !isDark() : args.theme === 'dark' ? true : args.theme === 'light' ? false : null;

          if (wanted === null) return failure('Unknown theme "' + args.theme + '". Use dark, light or toggle.');
          if (wanted === isDark()) return text('The site is already in ' + (wanted ? 'dark' : 'light') + ' mode.');

          // The button's own handler: it saves the choice and repaints the map tiles.
          const toggle = document.getElementById('dark-mode-toggle');

          if (toggle) toggle.click();
          else if (typeof window.__halalCalgarySetTheme === 'function') window.__halalCalgarySetTheme(wanted);
          else return failure('This page has no theme switch.');

          return text('Switched to ' + (isDark() ? 'dark' : 'light') + ' mode.');
        },
      },
    ];
  }

  return {
    register, text, failure, limitOf, siteTimeZone, absolute, commonTools,
    siteClock, openStatus, priceLevel, placePasses, tagCounts, withCatalog,
  };
})();


/* WEBMCP TOOLS - THE MAP
   Things the page already does, given a name. app.js calls install(ctx) with
   the live map - getters for what changes and the map's own functions
   (focusPlace, matchesSearch, searchScore, loadPlaceDetail, the form
   helpers) - so nothing is re-implemented and an agent's matches are the
   visitor's. ctx is the complete list of what the tools may touch. The words
   and schemas are in resources/webmcp/tools.json.

   THE FORM TOOLS DO NOT SEND BY DEFAULT. submit_place and report_listing fill
   and open the real modal, and send only with submit: true - sending emails
   the site owner about a real business, which an agent should ask for
   deliberately. */
window.hcMapTools = (function () {
  'use strict';

  /**
   * @param {{
   *   places: () => Array<object>,
   *   userLocation: () => ({lat: number, lng: number}|null),
   *   isReady: () => boolean,
   *   activeFilter: () => ({category: ?string, cuisine: ?string}),
   *   AMENITY_FIELDS: object, TAG_CATALOGUE: object, filterBtns: Array<Element>,
   *   submitModalForm: Element, reportModalForm: Element,
   *   slugify: Function, placeSlug: Function, placeUrl: Function, googleMapsLink: Function,
   *   appleMapsLink: Function, priceLabel: Function,
   *   haversineKm: Function, detailsText: Function, findPlaceBySlug: Function,
   *   focusPlace: Function, matchesSearch: Function, searchScore: Function,
   *   passesActiveCategories: Function, clearFilters: Function, sortTopRated: Function,
   *   loadPlaceDetail: Function, loadPlaceReviews: Function, fetchWithToken: Function,
   *   openSubmitModal: Function, openReportModal: Function,
   *   validateForm: Function, validatableFields: Function, fieldMessage: Function
   * }} ctx the live map, handed over by app.js
   * @returns {boolean} whether the tools were handed to register()
   */
  function install(ctx) {
    const agentTools = window.hcAgentTools;

    if (!agentTools || !ctx) return false;

    const {
      AMENITY_FIELDS, filterBtns, submitModalForm, reportModalForm,
      slugify, placeSlug, placeUrl, googleMapsLink, appleMapsLink, priceLabel,
      haversineKm, detailsText, findPlaceBySlug, focusPlace, matchesSearch, searchScore,
      loadPlaceDetail, loadPlaceReviews, fetchWithToken,
      openSubmitModal, openReportModal,
      validateForm, validatableFields, fieldMessage,
    } = ctx;
    const { text, failure, limitOf, openStatus, placePasses, siteTimeZone, absolute } = agentTools;
    const tags = ctx.TAG_CATALOGUE || { categories: {}, cuisines: {} };
    const round1 = km => Math.round(km * 10) / 10;

    /* The point distances are measured from: the one the caller gave, else
       the visitor's shared location, else none. */
    function originFrom(args) {
      if (args.near_lat !== undefined || args.near_lng !== undefined) {
        const lat = Number(args.near_lat);
        const lng = Number(args.near_lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
          throw new Error('near_lat and near_lng must be given together, as a latitude and a longitude.');
        }

        return { lat, lng };
      }

      return ctx.userLocation() || null;
    }

    function distanceFrom(origin, p) {
      return origin ? haversineKm(origin.lat, origin.lng, p.lat, p.lng) : null;
    }

    /* The hours of every place, for open now - fetched on first need (never at
       boot) and kept ten minutes. */
    let hoursCache = null;

    function loadHours() {
      if (hoursCache && Date.now() - hoursCache.at < 10 * 60 * 1000) return Promise.resolve(hoursCache.data);

      return fetchWithToken('/api/hours')
        .then(res => (res.ok ? res.json() : Promise.reject(new Error('The opening hours could not be loaded (HTTP ' + res.status + ').'))))
        .then((body) => {
          const data = { timezone: (body && body.timezone) || siteTimeZone(), hours: (body && body.hours) || {} };

          hoursCache = { at: Date.now(), data };

          return data;
        });
    }

    function isTemporarilyClosed(p) {
      return p.businessStatus === 'CLOSED_TEMPORARILY';
    }

    /** One place, reduced from ~60 renderer fields to what an agent can use. */
    function placeSummary(p, origin, hours) {
      const amenities = Object.keys(AMENITY_FIELDS).filter(key => p[AMENITY_FIELDS[key]]);
      const out = {
        name: p.name,
        slug: placeSlug(p),
        address: p.address || null,
        url: placeUrl(p),
        rating: typeof p.rating === 'number' ? p.rating : null,
        ratingCount: p.ratingCount || null,
        // The label the card shows (a price range where known), and the
        // 1-4 level max_price filters on.
        price: priceLabel(p) || null,
        priceLevel: agentTools.priceLevel(p) || null,
        cuisines: Array.isArray(p.cuisines) ? p.cuisines : [],
        amenities,
        featured: !!p.featured,
      };

      if (isTemporarilyClosed(p)) out.temporarilyClosed = true;

      // Absent rather than null when there is no point or no hours.
      const km = distanceFrom(origin === undefined ? ctx.userLocation() : origin, p);

      if (km !== null) out.distanceKm = round1(km);

      if (hours) {
        const status = openStatus(hours.hours[placeSlug(p)], new Date(), hours.timezone);

        if (status) out.openNow = status.open;
      }

      return out;
    }

    /** The tags a place wears, as the sheet's chips show them. */
    function tagsOf(p) {
      const categories = Object.keys(tags.categories || {})
        .filter(slug => p[tags.categories[slug].field])
        .map(slug => tags.categories[slug].label);
      const cuisines = Object.keys(tags.cuisines || {})
        .filter(slug => Array.isArray(p.cuisines) && p.cuisines.indexOf(slug) !== -1)
        .map(slug => tags.cuisines[slug].label);

      return { categories, cuisines };
    }

    /** The week as the sheet words it, with plain spaces. */
    function hoursLinesOf(p) {
      return Array.isArray(p.hoursLines) && p.hoursLines.length
        ? p.hoursLines.map(line => ({ day: line.label, hours: String(line.hours).replace(/\u00a0/g, ' ') }))
        : null;
    }

    async function placeDetail(p) {
      // The boot payload is a projection; the sheet's fields arrive on demand.
      await loadPlaceDetail(p);

      const links = {};

      ['facebook', 'instagram', 'ubereats', 'skipthedishes', 'doordash', 'openTableUrl'].forEach((key) => {
        if (typeof p[key] === 'string' && p[key].trim()) links[key === 'openTableUrl' ? 'opentable' : key] = p[key].trim();
      });

      return Object.assign(placeSummary(p), {
        phone: p.phone || null,
        website: p.website || null,
        description: detailsText(p.details) || p.summary || null,
        tags: tagsOf(p),
        hours: hoursLinesOf(p),
        links,
        lat: p.lat,
        lng: p.lng,
        googleMapsUrl: googleMapsLink(p),
        appleMapsUrl: appleMapsLink(p),
      });
    }

    /** slug or name, whichever the caller had to hand. */
    function resolvePlace(ref) {
      if (!ref) return null;

      const wanted = String(ref).trim();

      return findPlaceBySlug(wanted)
        || findPlaceBySlug(slugify(wanted))
        || ctx.places().find(p => p.name.toLowerCase() === wanted.toLowerCase())
        || null;
    }

    function requireData() {
      if (!ctx.isReady()) throw new Error('The place data has not finished loading yet - try again in a moment.');
    }

    const NO_PLACE = '". Use search_places to find the right slug.';

    /* Fills a field as a person would, with input/change events: .value alone
       skips the listeners, so the field is neither checked nor cleared of its
       error. */
    function setFieldValue(field, value) {
      if (!field || value === undefined || value === null) return false;

      field.value = String(value);
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));

      return true;
    }

    function byName(scope, name) {
      const quoted = window.CSS && CSS.escape ? CSS.escape(name) : String(name).replace(/["\\]/g, '\\$&');

      return scope.querySelector('[name="' + quoted + '"]');
    }

    /* BY NAME, WITHIN THE OPEN FIELD SET, AND WHATEVER IT ACTUALLY HAS: the
       submit modal holds both forms' fields under the same names (only the
       open set is enabled), and both are configured in /admin
       (App\Services\SubmitForm). Returns the names no field took. */
    function fillNamed(scope, values) {
      return Object.keys(values || {}).filter(name => !setFieldValue(byName(scope, name), values[name]));
    }

    /** The field errors showing now, so a tool can say WHY a submit failed. */
    function formErrors(form) {
      return validatableFields(form)
        .filter(f => f.getAttribute('aria-invalid') === 'true')
        .map((f) => {
          const label = f.closest('.submit-field');
          const name = label ? (dom.one('span', label) || {}).textContent : f.name;

          return (name || f.name).replace(/\s*\*\s*$/, '').trim() + ': ' + fieldMessage(f);
        });
    }

    function visibleCount() {
      return ctx.places().filter(p => ctx.passesActiveCategories(p)).length;
    }

    function filterButton(kind, slug) {
      const quoted = window.CSS && CSS.escape ? CSS.escape(slug) : slug;

      return kind === 'category'
        ? document.getElementById('sidebar-' + slug + '-category-btn')
          || document.querySelector('.amenity-category-btn[data-amenity="' + quoted + '"]')
        : document.querySelector('#cuisine-row .cuisine-tag[data-cuisine="' + quoted + '"]');
    }

    /* The filters the row offers, in catalogue order. A category hidden from
       the row (Site settings) keeps its page and its tags but has no tag to
       press, so it is no filter here. */
    function offered(kind) {
      const all = (kind === 'category' ? tags.categories : tags.cuisines) || {};

      return Object.keys(all).filter(slug => filterButton(kind, slug));
    }

    const mapTools = [
      {
        name: 'search_places',
        async execute(args) {
          requireData();

          const query = (args.query || '').trim();
          const wanted = Array.isArray(args.amenities) ? args.amenities : [];
          const bad = wanted.filter(key => !AMENITY_FIELDS[key]);

          if (bad.length) return failure('Unknown amenity: ' + bad.join(', ') + '. Valid values are: ' + Object.keys(AMENITY_FIELDS).join(', ') + '.');

          const origin = originFrom(args);
          const radiusKm = Number(args.radius_km) > 0 ? Number(args.radius_km) : 0;

          if (radiusKm && !origin) return failure('radius_km needs near_lat and near_lng, or the visitor\'s shared location (set_place_sort "closest" asks for it).');

          const maxPrice = args.max_price ? Math.min(Math.max(parseInt(args.max_price, 10) || 0, 1), 4) : 0;
          const hours = args.open_now ? await loadHours() : null;
          const filters = {
            query,
            amenities: wanted,
            cuisine: (args.cuisine || '').trim().toLowerCase(),
            featuredOnly: !!args.featured_only,
            maxPrice,
            radiusKm,
            openNow: !!args.open_now,
          };
          const env = {
            fields: AMENITY_FIELDS,
            matches: matchesSearch,
            slug: placeSlug,
            hours: hours ? hours.hours : {},
            now: new Date(),
            timeZone: hours ? hours.timezone : siteTimeZone(),
            distanceKm: p => distanceFrom(origin, p),
          };
          let results = ctx.places().filter(p => placePasses(p, filters, env));

          if (args.sortBy === 'rating') {
            // As the Top rated pill: the rating weighed by how many reviews
            // back it, against the whole map (sortTopRated() in app.js).
            results = ctx.sortTopRated(results, ctx.places());
          } else if (args.sortBy === 'distance' && origin) {
            results = results.sort((a, b) => distanceFrom(origin, a) - distanceFrom(origin, b));
          } else if (query) {
            results = results.sort((a, b) => searchScore(a, query) - searchScore(b, query));
          }

          const limit = limitOf(args.limit, 20, 100);

          return text({
            total: results.length,
            returned: Math.min(results.length, limit),
            places: results.slice(0, limit).map(p => placeSummary(p, origin, hours)),
          });
        },
      },
      {
        name: 'get_place',
        async execute(args) {
          requireData();

          const p = resolvePlace(args.place);

          if (!p) return failure('No place matches "' + args.place + NO_PLACE);

          return text(await placeDetail(p));
        },
      },
      {
        name: 'get_opening_hours',
        async execute(args) {
          requireData();

          const p = resolvePlace(args.place);

          if (!p) return failure('No place matches "' + args.place + NO_PLACE);

          const [, hours] = await Promise.all([loadPlaceDetail(p), loadHours()]);
          const week = hoursLinesOf(p);
          const status = openStatus(hours.hours[placeSlug(p)], new Date(), hours.timezone);
          const out = { place: p.name, slug: placeSlug(p), timezone: hours.timezone };

          if (!week && !status) return text(Object.assign(out, { known: false, note: 'No opening hours are listed for this place.' }));

          Object.assign(out, { known: true, week });

          if (status) {
            Object.assign(out, {
              openNow: status.open && !isTemporarilyClosed(p),
              alwaysOpen: status.alwaysOpen,
              now: status.now,
              closesAt: status.closesAt,
              opensAt: status.opensAt,
            });
          }

          if (isTemporarilyClosed(p)) out.note = 'This place is listed as temporarily closed.';

          return text(out);
        },
      },
      {
        name: 'get_place_reviews',
        async execute(args) {
          requireData();

          const p = resolvePlace(args.place);

          if (!p) return failure('No place matches "' + args.place + NO_PLACE);

          const reviews = await loadPlaceReviews(placeSlug(p));
          const limit = limitOf(args.limit, 10, 50);

          return text({
            place: p.name,
            rating: typeof p.rating === 'number' ? p.rating : null,
            total: reviews.length,
            returned: Math.min(reviews.length, limit),
            reviews: reviews.slice(0, limit).map(r => ({
              author: r.author || null,
              rating: Number(r.rating) || null,
              text: r.text || '',
              when: r.relative || null,
              publishedAt: r.publishedAt || null,
              url: r.uri || null,
            })),
          });
        },
      },
      {
        name: 'show_place_on_map',
        async execute(args) {
          requireData();

          const p = resolvePlace(args.place);

          if (!p) return failure('No place matches "' + args.place + NO_PLACE);

          // Not awaited: the panel opens on the map's moveend, which may never
          // arrive.
          focusPlace(p);

          return text('Opened ' + p.name + ' on the map. Its page is at ' + placeUrl(p));
        },
      },
      {
        name: 'set_place_sort',
        async execute(args) {
          const btn = filterBtns.find(b => b.dataset.sort === args.sort);

          if (!btn) return failure('Unknown sort "' + args.sort + '". Use default, rating or closest.');

          // Via the button: its handler asks for location and moves the
          // active pill.
          btn.click();

          if (args.sort === 'closest' && !ctx.userLocation()) return text('Asked the visitor for their location; the list will re-sort by distance once they allow it.');

          return text('Place list now sorted by "' + args.sort + '".');
        },
      },
      {
        name: 'list_categories',
        async execute() {
          requireData();

          const active = ctx.activeFilter().category;
          const all = ctx.places();

          return text({
            categories: offered('category').map((slug) => {
              const category = tags.categories[slug];

              return {
                slug,
                label: category.label,
                places: all.filter(p => p[category.field]).length,
                url: absolute(category.href),
                active: active === slug,
              };
            }),
          });
        },
      },
      {
        name: 'list_cuisines',
        async execute() {
          requireData();

          const active = ctx.activeFilter().cuisine;
          const all = ctx.places();

          return text({
            cuisines: offered('cuisine').map((slug) => {
              const cuisine = tags.cuisines[slug];

              return {
                slug,
                label: cuisine.label,
                places: all.filter(p => Array.isArray(p.cuisines) && p.cuisines.indexOf(slug) !== -1).length,
                url: absolute(cuisine.href),
                active: active === slug,
              };
            }),
          });
        },
      },
      {
        name: 'apply_map_filter',
        async execute(args) {
          requireData();

          const category = (args.category || '').trim();
          const cuisine = (args.cuisine || '').trim();

          if ([category, cuisine, args.clear ? 'clear' : ''].filter(Boolean).length !== 1) {
            return failure('Give exactly one of category, cuisine or clear: true.');
          }

          if (args.clear) {
            ctx.clearFilters();

            return text({ filter: null, showing: visibleCount(), url: window.location.href });
          }

          const kind = category ? 'category' : 'cuisine';
          const slug = category || cuisine;
          const btn = filterButton(kind, slug);

          if (!btn) {
            const valid = offered(kind);

            return failure('This map has no ' + kind + ' filter "' + slug + '". Valid values are: ' + valid.join(', ') + '.');
          }

          // The tag toggles, so pressing the active one would clear it.
          if (ctx.activeFilter()[kind] !== slug) btn.click();

          return text({ filter: { kind, slug }, showing: visibleCount(), url: window.location.href });
        },
      },
      {
        name: 'list_featured_places',
        async execute() {
          requireData();

          const featured = ctx.places().filter(p => p.featured);

          return text({ total: featured.length, places: featured.map(p => placeSummary(p)) });
        },
      },
      {
        name: 'submit_place',
        async execute(args) {
          const type = args.request_type === 'feature_request' ? 'feature_request' : 'new_restaurant';
          const set = submitModalForm.querySelector('[data-submit-set="' + type + '"]');

          if (!set) return failure(type === 'feature_request' ? 'This site is not taking feature requests right now.' : 'The submission form is not on this page.');

          openSubmitModal(type);

          // Everything but the tool's own two arguments is a field name.
          const { request_type: _type, submit: _send, ...values } = args || {};
          const unused = fillNamed(set, values);

          if (!validateForm(submitModalForm)) return failure('The form is not valid yet: ' + formErrors(submitModalForm).join('; '));

          const note = unused.length ? ' (This form has no field for: ' + unused.join(', ') + '.)' : '';

          if (!args.submit) return text('Filled in the submission form for "' + args.restaurant_name + '" and opened it. Call again with submit: true to send it.' + note);

          submitModalForm.requestSubmit();

          return text('Sent the submission for "' + args.restaurant_name + '". The result appears in the open dialog.' + note);
        },
      },
      {
        name: 'report_listing',
        async execute(args) {
          requireData();

          const p = resolvePlace(args.place);

          if (!p) return failure('No place matches "' + args.place + NO_PLACE);

          // Fills the hidden listing name and page URL fields.
          openReportModal(p);
          setFieldValue(document.getElementById('rf-details'), args.details);

          if (!validateForm(reportModalForm)) return failure('The report is not valid yet: ' + formErrors(reportModalForm).join('; '));

          if (!args.submit) return text('Filled in a report about "' + p.name + '" and opened it. Call again with submit: true to send it.');

          reportModalForm.requestSubmit();

          return text('Sent the report about "' + p.name + '". The result appears in the open dialog.');
        },
      },
    ];

    agentTools.register(mapTools.concat(agentTools.commonTools()));

    return true;
  }

  return { install };
})();

// ---- WebMCP tools - articles ----
/* List, search and read the guides, and browse them by tag. The index is the
   same /api/articles the search box uses (same token), titles, excerpts and
   tag slugs; read_article fetches a body on demand. The tags themselves are
   the Tags menu's chips, on every articles route. Registered on every
   articles route, so an agent on one guide can find the others. */
(function () {
  // The map route registers its own set.
  if (!document.body.classList.contains('articles-mode')) return;

  /* Registration waits for DOMContentLoaded so it lands after every script,
     including a modelContext polyfill loaded by a later tag. */
  const ORIGIN = window.location.origin;
  const INDEX = '/articles/';
  const TAG_PATH = /^\/articles\/tags\/([a-z0-9-]+)\/$/;

  function token() {
    const meta = document.querySelector('meta[name="hc-token"]');

    return meta ? meta.content : '';
  }

  // Not articles.js's loadIndex(): that exists only on the index page. Not
  // memoised, so a long-lived tab never serves a stale list.
  function fetchIndex() {
    return fetch('/api/articles', {
      cache: 'no-store',
      headers: token() ? { 'X-HC-Token': token() } : {},
    })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('The article index could not be loaded (HTTP ' + res.status + ').'))))
      .then(list => (Array.isArray(list) ? list : []));
  }

  function summary(article) {
    return {
      slug: article.slug,
      title: article.title,
      excerpt: article.excerpt || null,
      tags: Array.isArray(article.tags) ? article.tags : [],
      publishedAt: article.publishedAt || null,
      url: ORIGIN + '/articles/' + encodeURIComponent(article.slug) + '/',
    };
  }

  // Title prefix, title, then excerpt - the same ranking as score() in
  // articles.js's search, so agent and visitor get the same first result.
  function rank(article, q) {
    const title = (article.title || '').toLowerCase();
    const excerpt = (article.excerpt || '').toLowerCase();

    if (title.startsWith(q)) return 0;
    if (title.includes(q)) return 1;
    if (excerpt.includes(q)) return 2;

    return -1;
  }

  /* The tags as the Tags menu shows them (partials/articles-menu): Site
     settings order, only tags some guide carries. A chip shows the short
     name; its title holds the full label. */
  function menuTags() {
    return Array.prototype.slice.call(document.querySelectorAll('#articles-menu .articles-chip'))
      .map((chip) => {
        const m = TAG_PATH.exec(chip.getAttribute('href') || '');

        if (!m) return null;

        const name = chip.querySelector('.articles-chip-name');
        const short = (name ? name.textContent : '').replace(/\s+/g, ' ').trim();

        return { slug: m[1], label: chip.getAttribute('title') || short, short, chip };
      })
      .filter(Boolean);
  }

  function tagUrl(slug) {
    return ORIGIN + (slug ? '/articles/tags/' + slug + '/' : INDEX);
  }

  /** A tag argument checked against the menu: the slug, '' for none, or null if unknown. */
  function knownTag(value, tags) {
    const slug = String(value || '').trim().toLowerCase();

    if (!slug) return '';

    return tags.some(t => t.slug === slug) ? slug : null;
  }

  function unknownTag(value, tags) {
    return 'No tag "' + value + '". Valid tags are: ' + tags.map(t => t.slug).join(', ') + '.';
  }

  function registerArticleTools() {
  const agentTools = window.hcAgentTools;

  if (!agentTools) return;

  const { text, failure, limitOf, tagCounts } = agentTools;

  const articleTools = [
    {
      name: 'list_articles',
      async execute(args) {
        const tags = menuTags();
        const tag = knownTag(args.tag, tags);

        if (tag === null) return failure(unknownTag(args.tag, tags));

        const list = (await fetchIndex()).filter(a => !tag || (Array.isArray(a.tags) && a.tags.indexOf(tag) !== -1));
        const limit = limitOf(args.limit, 20, 100);

        return text({
          tag: tag || null,
          total: list.length,
          returned: Math.min(list.length, limit),
          articles: list.slice(0, limit).map(summary),
        });
      },
    },
    {
      name: 'search_articles',
      async execute(args) {
        const q = String(args.query || '').trim().toLowerCase();

        if (!q) return failure('search_articles needs a query. Use list_articles to see everything.');

        const tags = menuTags();
        const tag = knownTag(args.tag, tags);

        if (tag === null) return failure(unknownTag(args.tag, tags));

        const list = await fetchIndex();
        const hits = list
          .filter(a => !tag || (Array.isArray(a.tags) && a.tags.indexOf(tag) !== -1))
          .map(article => ({ article, score: rank(article, q) }))
          .filter(hit => hit.score !== -1)
          .sort((a, b) => a.score - b.score);
        const limit = limitOf(args.limit, 10, 100);

        return text({
          query: args.query,
          tag: tag || null,
          total: hits.length,
          returned: Math.min(hits.length, limit),
          articles: hits.slice(0, limit).map(hit => summary(hit.article)),
        });
      },
    },
    {
      name: 'read_article',
      async execute(args) {
        const slug = String(args.slug || '').trim();

        if (!slug) return failure('read_article needs a slug. Use search_articles to find one.');

        const url = '/articles/' + encodeURIComponent(slug) + '/';
        const res = await fetch(url, { cache: 'no-store' });

        if (!res.ok) return failure('No article at "' + slug + '" (HTTP ' + res.status + '). Use search_articles to find the right slug.');

        /* PARSED, NOT REGEXED: pick .article-body out of the whole page.
           DOMParser's document is inert - no scripts, no image fetches. */
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const body = doc.querySelector('.article-body');
        const heading = doc.querySelector('.article-header h1, .article-body h1, h1');

        if (!body) return failure('The page at "' + slug + '" has no article body on it.');

        return text({
          slug,
          title: heading ? heading.textContent.trim() : (doc.title || slug),
          url: ORIGIN + url,
          text: body.textContent.replace(/\n{3,}/g, '\n\n').trim(),
        });
      },
    },
    {
      name: 'list_article_tags',
      async execute() {
        const tags = menuTags();
        const list = await fetchIndex();
        const counts = tagCounts(list, tags.map(t => t.slug));
        const active = (TAG_PATH.exec(window.location.pathname) || [])[1] || null;

        return text({
          totalArticles: list.length,
          tags: tags.map(t => ({
            slug: t.slug,
            label: t.label,
            short: t.short,
            articles: counts[t.slug],
            url: tagUrl(t.slug),
            active: active === t.slug,
          })),
        });
      },
    },
    {
      name: 'show_article_tag',
      async execute(args) {
        const tags = menuTags();
        const tag = knownTag(args.tag, tags);

        if (tag === null) return failure(unknownTag(args.tag, tags));

        const path = tag ? '/articles/tags/' + tag + '/' : INDEX;
        const here = window.location.pathname;
        const current = here === INDEX ? '' : ((TAG_PATH.exec(here) || [])[1] || null);
        const onIndex = !!document.querySelector('[data-articles-list]') && current !== null;

        if (onIndex) {
          if (current === tag) return text('Already showing ' + (tag ? 'the "' + tag + '" guides' : 'the categories') + '.');

          // The chip's own handler filters in place and moves the URL; a
          // picked chip clicked again would clear, hence the check above.
          const chip = Array.prototype.slice.call(document.querySelectorAll('#articles-menu .articles-chip'))
            .find(a => a.getAttribute('href') === path);

          if (chip) {
            chip.click();

            return text({
              // The index is the featured guides and the category cards, not a list of all.
              showing: tag || 'categories',
              // Cards in a hidden part (the featured row, on a tag) are not shown either.
              visible: Array.prototype.filter.call(
                document.querySelectorAll('[data-articles-list] .article-card'),
                card => !card.closest('[hidden]'),
              ).length,
              url: tagUrl(tag),
            });
          }
        }

        // Elsewhere the chips are plain links: go there, as a click would.
        setTimeout(() => window.location.assign(path), 0);

        return text('Opening ' + tagUrl(tag) + ' - this page is replaced, and the tools register again there.');
      },
    },
  ];

  agentTools.register(articleTools.concat(agentTools.commonTools()));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerArticleTools, { once: true });
  } else {
    registerArticleTools();
  }
})();
